'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { http } from '../../lib/api/http';
import {
  Favorite,
  FavoriteKinds,
  FavoriteKindsV5,
  FavoriteV5,
  FavoriteV5Short,
} from '../../models/favorite';
import { V5 } from '../../lib/api/urls';
import { useAuthorization } from '../authorization';
import {
  FAVORITES_ACTIONS,
  getFavoriteKind,
  UpdateFavoritesSignature,
} from './index.model';
import { Action, Material, Person, Place } from '../../models';

const FavoriteContext = createContext<{
  getCount: () => number;
  getFavId: (
    itemId: string | number,
    kind: keyof typeof FavoriteKinds
  ) => string | undefined;
  updateFavorite: UpdateFavoritesSignature;
  getFavoritesList: () => Favorite[];
  getFilteredList: (kind: keyof typeof FavoriteKinds) => {
    [key: number]: Action | Material | Person | Place;
  };
} | null>(null);

const defaultFavList = {
  [FavoriteKinds.event]: {},
  [FavoriteKinds.place]: {},
  [FavoriteKinds.person]: {},
  [FavoriteKinds.material]: {},
};

export function FavoriteProvider({ children }: PropsWithChildren) {
  const authContext = useAuthorization();
  const [favoriteList, setFavoriteList] = useState<{
    [FavoriteKinds.event]: { [key: number]: number };
    [FavoriteKinds.place]: { [key: number]: number };
    [FavoriteKinds.person]: { [key: number]: number };
    [FavoriteKinds.material]: { [key: number]: number };
  }>(defaultFavList);

  const getFavId = useCallback(
    (itemId: string | number, kind: keyof typeof FavoriteKinds) =>
      Object.keys(favoriteList[kind]).find(
        (key) => Number(favoriteList[kind][Number(key)]) === Number(itemId)
      ),
    [favoriteList]
  );

  const getCount = useCallback(
    () =>
      Object.values(favoriteList)
        .map((kindItem) => Object.values(kindItem))
        .flat().length,
    [favoriteList]
  );

  function updateFavoritesList(
    action: keyof typeof FAVORITES_ACTIONS,
    kind: keyof typeof FavoriteKinds,
    id: number,
    object_id?: number
  ) {
    if (action === FAVORITES_ACTIONS.ADD) {
      setFavoriteList((oldState) => ({
        ...oldState,
        [kind]: {
          ...oldState[kind],
          [id]: object_id,
        },
      }));
    }
    if (action === FAVORITES_ACTIONS.DELETE) {
      setFavoriteList((oldState) => {
        const newState = { ...oldState };
        delete newState[kind][id];
        return newState;
      });
    }
  }

  const fetchFavorites = async () => {
    const limit = 20;
    let offset = 0;
    let hasMore = true;

    try {
      while (hasMore) {
        const response: { next: null | string; results: FavoriteV5[] } =
          // eslint-disable-next-line no-await-in-loop
          await http.get(
            V5.favoritesShort,
            {
              brief: true,
              limit,
              offset,
            },
            null,
            { contentType: 'json', withTokenRequest: true }
          );

        if (response.results && response.results.length > 0) {
          response.results.forEach((favItem) => {
            const kind = getFavoriteKind(favItem);
            if (kind) {
              updateFavoritesList(
                FAVORITES_ACTIONS.ADD,
                kind,
                favItem.id,
                favItem.object_id
              );
            }
          });
        }

        if (response.next) {
          offset += limit;
        } else {
          hasMore = false;
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке избранного:', error);
      throw error;
    }
  };

  const updateFavorite: UpdateFavoritesSignature = useCallback(
    (itemId, kind) => {
      const favId = getFavId(itemId, kind);
      if (favId) {
        return http
          .delete(V5.favoriteItem(favId), undefined, undefined, {
            contentType: 'json',
            withTokenRequest: true,
          })
          .then((response) => {
            if (response.status === 204) {
              updateFavoritesList(
                FAVORITES_ACTIONS.DELETE,
                kind,
                Number(favId)
              );
            }
          });
      }
      return http
        .post(
          V5.favorites,
          { object_id: itemId, content_type: FavoriteKindsV5[kind] },
          undefined,
          {
            contentType: 'json',
            withTokenRequest: true,
          }
        )
        .then((response: { status: number; data: FavoriteV5Short }) => {
          if (response.status === 201) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            updateFavoritesList(
              FAVORITES_ACTIONS.ADD,
              kind,
              response.data.id,
              response.data.object_id
            );
          }
        });
    },
    [getFavId]
  );
  const getFilteredList = useCallback(
    (kind: keyof typeof FavoriteKinds) => favoriteList[kind],
    [favoriteList]
  );

  const getFavoritesList = useCallback(
    () => Object.values(favoriteList),
    [favoriteList]
  );
  useEffect(() => {
    setFavoriteList(defaultFavList);
    if (authContext.getUser()) {
      fetchFavorites();
    }
  }, [authContext.getUser()]);
  const contextValue = useMemo(
    () => ({
      updateFavorite,
      getFavoritesList,
      getFilteredList,
      getCount,
      getFavId,
    }),
    [updateFavorite, getFavoritesList, getFilteredList, getCount, getFavId]
  );

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <FavoriteContext.Provider value={contextValue}>
      {children}
    </FavoriteContext.Provider>
  );
}

export function useFavorite() {
  return useContext(FavoriteContext);
}
